import type { CallScript } from "@frontierx/shared/schemas/CallScript";
import type { ScriptDetectionLogPayload } from "@frontierx/shared/schemas/clientMessageTypes";
import type { LogMessage } from "@frontierx/types";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useAnalytics } from "../components/Analytics/AnalyticsProvider";
import { useAuth } from "../components/Auth/UnifiedAuthProvider";
import { useResetCallStore } from "../stores/callStore";
import { apiClient } from "../utils/apiClient";
import { useAppPartySocket } from "./useAppPartySocket";
import { useCallTransitionMonitor } from "./useCallTransitionMonitor";

export function useCallManager() {
  const { trackEvent } = useAnalytics();
  const queryClient = useQueryClient();
  const resetCallStore = useResetCallStore();
  const [selectedCall, setSelectedCall] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<LogMessage[]>([]);
  const [scriptDetectionLogs, setScriptDetectionLogs] = useState<
    ScriptDetectionLogPayload[]
  >([]);
  const {
    organization,
    user,
    isAuthenticated,
    isLoading: _isAuthLoading,
    isReady,
  } = useAuth();

  // Use TanStack Query for calls data with stale-while-revalidate
  const {
    data: calls = [],
    isLoading,
    isFetching,
    refetch: refreshCalls,
  } = useQuery({
    queryKey: ["calls", organization?.id],
    queryFn: async () => {
      if (!organization?.id) throw new Error("No organization ID");

      const fromDate = new Date(Date.now() - 24 * 60 * 60 * 1000); // 1 day ago
      const toDate = new Date(Date.now() + 24 * 60 * 60 * 1000); // 1 day from now
      console.log("[web/useCallManager] Making API call", {
        orgId: organization.id,
        from: fromDate.toISOString(),
        to: toDate.toISOString(),
        limit: 100,
      });

      const response = await apiClient.getCalls({
        orgId: organization.id,
        // Get calls from yesterday to tomorrow (±1 day window)
        from: fromDate,
        to: toDate,
        limit: 100,
      });

      console.log("[web/useCallManager] API response received", {
        callsCount: response.calls.length,
        hasMore: response.pagination?.hasMore,
        sampleCalls: response.calls.slice(0, 3).map((call) => ({
          id: call.id,
          status: call.status,
          startTime: call.startTime,
          callEndedAt: call.callEndedAt,
        })),
      });

      return response.calls;
    },
    enabled: !!organization?.id && isAuthenticated && isReady,
    placeholderData: keepPreviousData, // Keep previous data while revalidating
    refetchInterval: (query) => {
      // More frequent updates if we have upcoming calls to catch time-sensitive changes
      const data = query?.state?.data;
      const hasUpcomingCalls = data?.some(
        (call) =>
          call.status === "scheduled" && new Date(call.startTime) > new Date(),
      );
      // More aggressive polling when calls are about to start (30 seconds vs 2 minutes)
      return hasUpcomingCalls ? 30000 : 120000; // 30 seconds if upcoming calls, otherwise 2 minutes
    },
    refetchIntervalInBackground: true, // Continue refetching when window is not focused
    staleTime: 30 * 1000, // Consider data stale after 30 seconds for time-sensitive upcoming calls
  });

  // Monitor call transitions to ensure calls don't disappear when moving from "upcoming" to "now"
  useCallTransitionMonitor(calls, organization?.id);

  // Socket for real-time updates - always call hook but pass organization ID
  // The hook will handle the null case internally without connecting
  const mainSocket = useAppPartySocket({
    room: organization?.id ?? "disabled",
    party: "main",
    onMessage(event) {
      // Skip message handling if no organization
      if (!organization?.id) return;

      // Handle WebSocket ping/pong frames - these are not JSON messages
      if (event.data === "ping") {
        // WebSocket will automatically handle this, but we can respond manually
        return;
      }
      if (event.data === "pong") {
        return;
      }

      const data = JSON.parse(event.data);
      // Reduced logging - only log important events
      if (data.type === "CALL_CREATED" || data.type === "CALL_DELETED") {
        console.log("Received message:", data.type);
      }
      switch (data.type) {
        case "ACTIVE_CALLS":
          // Invalidate calls query to trigger background refetch
          queryClient.invalidateQueries({
            queryKey: ["calls", organization?.id],
          });
          break;
        case "CALL_CREATED":
          // Invalidate calls query to trigger background refetch
          queryClient.invalidateQueries({
            queryKey: ["calls", organization?.id],
          });
          // Only set selectedCall if we're not currently in a call
          if (!selectedCall) {
            setSelectedCall(data.payload.id);
          }
          break;
        case "CALL_DELETED":
          // Invalidate calls query to trigger background refetch
          queryClient.invalidateQueries({
            queryKey: ["calls", organization?.id],
          });
          if (selectedCall === data.payload.roomId) {
            setSelectedCall(null);
          }
          break;
        case "CALL_CREATION_ERROR":
          console.error("Call creation error:", data.payload.error);
          setError(data.payload.error);
          trackEvent("call_creation_error", {
            error: data.payload.error,
          });
          break;
        case "CALL_UPDATED":
          // Update a single call in the list or refresh all calls
          refreshCalls();
          break;
        case "LOG":
          setLogs((prevLogs) => [...prevLogs, data.payload]);
          console.log("Server log:", data.payload.message);
          break;
        case "SCRIPT_DETECTION_LOG":
          setScriptDetectionLogs((prevLogs) => [...prevLogs, data.payload]);
          console.log("Script detection log:", data.payload);
          break;
        case "CALENDAR_SYNC_COMPLETE":
          console.log("📅 Calendar sync complete - refreshing calls");
          queryClient.invalidateQueries({
            queryKey: ["calls", organization?.id],
          });
          break;
      }
    },
  });

  // Manual refresh function for UI buttons
  const handleRefresh = useCallback(() => {
    refreshCalls();
  }, [refreshCalls]);

  // Create a new call
  const createCall = useCallback(
    async (url: string, callScript: CallScript) => {
      setError(null);

      try {
        if (!user?.id || !organization?.id) {
          throw new Error("User or organization not available");
        }

        // Calculate start/end times (default to 1 hour)
        const startTime = new Date();
        const endTime = new Date(startTime.getTime() + 60 * 60 * 1000);

        const createdCall = await apiClient.createCall({
          userId: user.id,
          orgId: organization.id,
          meetingUrl: url,
          summary: "Ad-hoc call",
          startTime: startTime.toISOString(),
          endTime: endTime.toISOString(),
          callScriptId: callScript.id,
        });

        // Track the call creation event
        trackEvent("call_created", {
          roomId: createdCall.id,
          url: url,
          scriptId: callScript.id,
        });

        // Refresh calls after creation
        refreshCalls();

        // Set the selected call to the newly created one
        setSelectedCall(createdCall.id);
      } catch (err) {
        console.error("Error creating call via API:", err);
        setError(err instanceof Error ? err.message : "Failed to create call");
      }
    },
    [organization?.id, user?.id, refreshCalls, trackEvent],
  );

  // Delete a call
  const deleteCall = useCallback(
    async (roomId: string) => {
      try {
        await apiClient.deleteCall(roomId);

        // Update UI state after deletion
        refreshCalls();
        if (selectedCall === roomId) {
          setSelectedCall(null);
        }

        // Track deletion event
        trackEvent("call_deleted", {
          roomId: roomId,
        });
      } catch (err) {
        console.error("Error deleting call via API:", err);
        setError(err instanceof Error ? err.message : "Failed to delete call");
      }
    },
    [refreshCalls, selectedCall, trackEvent],
  );

  const selectCall = useCallback(
    (roomId: string) => {
      if (roomId === selectedCall) {
        // No-op if selecting the same call
        return;
      }

      setSelectedCall(roomId);

      // Reset both stores to clear previous call data when switching calls
      if (
        import.meta.env.VITE_ENVIRONMENT === "local" ||
        import.meta.env.VITE_ENVIRONMENT === "dev"
      ) {
        console.log(
          "[CallManager] Resetting call and script stores when selecting new call:",
          roomId,
        );
      }
      resetCallStore();

      trackEvent("user_selected_call", { roomId });
    },
    [selectedCall, trackEvent, resetCallStore],
  );

  const leaveCall = useCallback(async () => {
    if (selectedCall) {
      try {
        // Find the selected call to check its status
        const callRecord = calls.find((call) => call.id === selectedCall);

        // Trigger analysis based on call status:
        // - "processed": Analysis complete, no need to trigger
        // - "in_progress": Call ended but analysis may be running or not started yet
        // - Other statuses: Call not ready for analysis
        if (callRecord?.status === "processed") {
          console.log(
            `Call ${selectedCall} is already processed with analysis complete, skipping trigger`,
          );
          trackEvent("call_analysis_skipped", {
            roomId: selectedCall,
            reason: "analysis_already_complete",
            callStatus: callRecord.status,
          });
        } else if (callRecord?.status === "in_progress") {
          console.log(
            `Call ${selectedCall} is in progress, triggering analysis (backend will prevent duplicates)`,
          );
          await apiClient.triggerCallAnalysis(selectedCall);
          trackEvent("call_analysis_triggered", {
            roomId: selectedCall,
            trigger: "leave_call",
            callStatus: callRecord.status,
          });
        } else {
          console.log(
            `Call ${selectedCall} is not ready for analysis (status: ${callRecord?.status}), skipping trigger`,
          );
          trackEvent("call_analysis_skipped", {
            roomId: selectedCall,
            reason: "call_not_ready",
            callStatus: callRecord?.status || "unknown",
          });
        }
      } catch (err) {
        console.error("Error triggering call analysis:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to trigger call analysis",
        );
      }
    }

    // Reset both stores to clear transcript and other call-specific data
    if (
      import.meta.env.VITE_ENVIRONMENT === "local" ||
      import.meta.env.VITE_ENVIRONMENT === "dev"
    ) {
      console.log(
        "[CallManager] Resetting call and script stores when leaving call",
      );
    }
    resetCallStore();

    setSelectedCall(null);
    trackEvent("user_returned_to_all_calls");
  }, [selectedCall, trackEvent, calls, resetCallStore]);

  const swapCallScript = useCallback(
    async (callId: string, callScript: CallScript) => {
      try {
        // Update the call with the new script ID
        await apiClient.updateCall(callId, {
          callScriptId: callScript.id,
        });

        // Refresh calls to update the UI
        queryClient.invalidateQueries({
          queryKey: ["calls", organization?.id],
        });

        // Track script swap event
        trackEvent("call_script_swapped", {
          callId: callId,
          scriptId: callScript.id,
        });
      } catch (err) {
        console.error("Error swapping call script via API:", err);
        setError(
          err instanceof Error ? err.message : "Failed to swap call script",
        );
      }
    },
    [organization?.id, queryClient, trackEvent],
  );

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  const clearScriptDetectionLogs = useCallback(() => {
    setScriptDetectionLogs([]);
  }, []);

  return {
    calls,
    selectedCall,
    error,
    logs,
    scriptDetectionLogs,
    isLoading,
    isFetching,
    createCall,
    deleteCall,
    selectCall,
    leaveCall,
    clearLogs,
    clearScriptDetectionLogs,
    swapCallScript,
    refreshCalls: handleRefresh,
    mainSocket,
  };
}
